import type { ReactNode } from 'react'
import { useT } from '../../i18n'
import { IS_ADMIN_ORIGIN } from '../../lib/origin-shell'
import type { AuthPresentationSurface } from '../../api/generated/model'
import { AUTH_COPY } from './auth-copy'
import { useAuthPresentation } from './useAuthPresentation'

type AuthShellProps = {
  title: string
  description: string
  children: ReactNode
}

export function AuthShell({ title, description, children }: AuthShellProps) {
  const t = useT(AUTH_COPY)
  const surface: AuthPresentationSurface = IS_ADMIN_ORIGIN ? 'admin' : 'app'
  const story = t[surface]
  const media = useAuthPresentation(surface)

  return (
    <main className="pex-auth-shell">
      <section className="pex-auth-story" aria-label={story.surface}>
        <img
          className="pex-auth-story-media"
          src={media.heroUrl}
          alt={media.altText}
          style={{ objectPosition: `${media.focalX}% ${media.focalY}%` }}
          width={1536}
          height={1024}
          onError={(event) => {
            if (!event.currentTarget.src.endsWith(media.fallbackUrl)) {
              event.currentTarget.src = media.fallbackUrl
            }
          }}
        />
        <div className="pex-auth-story-overlay" />
        <img
          className="pex-auth-story-lockup"
          src={`${import.meta.env.BASE_URL}brand/pexify-product-lockup-light.svg`}
          alt="Pexify"
        />
        <div className="pex-auth-story-copy">
          <p className="pex-auth-eyebrow">{story.eyebrow}</p>
          <h2>{story.headline}</h2>
          <p>{story.body}</p>
        </div>
        <div className="pex-auth-proof-strip" aria-label={t.proofsLabel}>
          {story.proofs.map((proof) => (
            <span key={proof}>{proof}</span>
          ))}
        </div>
      </section>

      <section className="pex-auth-form-panel">
        <div className="pex-auth-form-wrap">
          <header className="pex-auth-form-header">
            <img
              className="pex-auth-wordmark-on-light"
              src={`${import.meta.env.BASE_URL}brand/pexify-wordmark-titlecase.svg`}
              alt="Pexify"
            />
            <img
              className="pex-auth-wordmark-on-dark"
              src={`${import.meta.env.BASE_URL}brand/pexify-wordmark-titlecase-light.svg`}
              alt="Pexify"
            />
            <span>{story.surface}</span>
          </header>
          <div className="pex-auth-form-intro">
            <h1>{title}</h1>
            <p>{description}</p>
          </div>
          {children}
          <footer className="pex-auth-footer">
            <a href="mailto:hello@pexify.net">{t.support}</a>
            <span aria-hidden="true">·</span>
            <a href="https://pexify.net/terms">{t.terms}</a>
            <span aria-hidden="true">·</span>
            <a href="https://pexify.net/privacy">{t.privacy}</a>
          </footer>
        </div>
      </section>
    </main>
  )
}
